import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, Optional, Self, ViewChild } from '@angular/core';

import { DsAbstractControlComponent } from '@abstract/components/ds-abstract-control.component';

/**
 * Reusable input component for Angular Material with support for:
 * - reactive forms (`FormControl` / `NgControl`)
 * - custom error state handling (immediate error display)
 * - prefix/suffix content projection
 * - input attributes like placeholder, type, maxlength, minlength, autocomplete
 *
 * Automatically integrates with Angular Material's `<mat-form-field>`
 * error styling and supports showing icons or hints based on the form control state.
 *
 * @remarks Doesn't support ngModel
 */
@Component({
  selector: 'ds-input',
  templateUrl: './ds-input.component.html',
  styleUrls: ['./ds-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, MatIconModule, MatInputModule, ReactiveFormsModule],
})
export class DsInputComponent extends DsAbstractControlComponent {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('input') private readonly inputRef?: ElementRef<HTMLInputElement>;

  /** Input type (text, email, password, etc.) */
  @Input() type = 'text';

  /** Whether the input is readonly */
  @Input() readonly = false;

  /** HTML autocomplete attribute */
  @Input() autocomplete?: string;

  /** HTML name attribute */
  @Input() name?: string;

  /** Maximum allowed length for input value */
  @Input() maxlength?: number;

  /** Minimum allowed length for input value */
  @Input() minlength?: number;

  /** Maximum numeric value */
  @Input() max?: number | string;

  /** Minimum numeric value */
  @Input() min?: number | string;

  /** Whether the input is disabled */
  @Input()
  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;

    if (!this.ngControl && isDisabled) {
      this.defaultControl.disable();
    }
  }

  get disabled(): any {
    return this._disabled;
  }

  private _disabled: any;

  /** Internal input value **/
  @Input()
  set value(val: unknown) {
    this._value = this.validateInputValue(val);

    if (!this.ngControl) {
      this.defaultControl.setValue(this._value, { emitEvent: false });
    }
  }

  get value(): any {
    return this._value;
  }

  private _value: any;

  readonly defaultControl = new FormControl();

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(obj: unknown): void {
    this._value = this.validateInputValue(obj);

    if (!this.ngControl) {
      this.defaultControl.setValue(this._value, { emitEvent: false });
    }

    this.changeDetectorRef.markForCheck();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  handleInput(value: string): void {
    const coercedValue: string | number | null = this.validateInputValue(value);

    this._value = coercedValue;
    this.onChange(coercedValue);
    this.onTouched();
  }

  /**
   * Focuses the native input element.
   */
  focus(): void {
    this.inputRef?.nativeElement.focus();
  }

  /**
   * Validates DOM input values for `type="number"` to `null` when empty and to numbers when valid.
   *
   * @param value - raw value from the input or Angular forms API
   */
  private validateInputValue(value: unknown): string | number | null {
    if (this.type !== 'number') {
      return value as string;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    const trimmedValue: string = String(value).trim();

    if (trimmedValue.length === 0) {
      return null;
    }

    const parsedValue: number = Number(trimmedValue);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  get control(): FormControl {
    return (this.ngControl?.control ?? this.defaultControl) as FormControl;
  }
}
